/* eslint-disable @typescript-eslint/naming-convention */
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { DTypeRepository, DTypeStatus, Pagination, Partaker, SelectOption } from '@grupo-constant/next-dtype/types';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import * as lodash from 'lodash-es';
import { combineLatest, debounceTime, firstValueFrom, merge, Subject, takeUntil } from 'rxjs';
import { Net2BeepleLocator } from '../dtype.types';
import { LocalizePipe } from '../../pipes/standalone/localize.pipe';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { DTypeService } from '../dtype.service';
import { HttpService } from '../../http/http.service';
import { Net2BeepleMethodsEnum } from '../../enums/net2beeple-methods.enum';
import { Utils } from '../../utils/utils';
import { OU_URI } from '../../../modules/admin/ou/ou.types';
import { HttpServiceEndpointUri, MasterEndpointTargetSystemUri } from '../../../modules/admin/endpoints/endpoints.types';
import { MASTER_URI } from '../../master/master.types';
export const UPLOAD_GOAL_EXCEL_URI = 'excelupload://com.cloudcodice.thirdparty.grupoconstant.poixssf.DTypeExcelUpload';
@Component({
    selector: 'dtype-facade-list',
    templateUrl: './dtype-facade-list.component.html',
    imports: [CommonModule, FormsModule, LocalizePipe, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatPaginatorModule, MatProgressBarModule, MatTooltipModule, ReactiveFormsModule, RouterModule, TranslocoModule, MatSortModule, MatPaginatorModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [LocalizePipe]
})
export class DTypeFacadeListComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild(MatSort) _sort: MatSort;
    @ViewChild(MatPaginator) _paginator: MatPaginator;

    repoList: DTypeRepository[] = [];
    filteredRepoList: DTypeRepository[] = [];
    viewList: DTypeRepository[] = [];
    locator: string;
    captionLocator: string;
    facade: string;
    params: Params;

    searchControl = new FormControl('');
    readonly debounceTime = 400;

    pagination: Pagination;
    loading = true;

    typologies: SelectOption[];

    private initialized = new Subject<void>();
    private unsubscribeAll = new Subject<void>();
    private readonly masterTypologyClass = 'com.cloudcodice.feature.master.MasterTypology';
    private readonly tasktypeTypologyClass = 'com.cloudcodice.thirdparty.grupoconstant.nexttasktype.NextTaskTypeTypology';

    private changeDetector = inject(ChangeDetectorRef);
    private dialogsService = inject(DialogsService);
    private dtypeService = inject(DTypeService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private localize = inject(LocalizePipe);
    private transloco = inject(TranslocoService);
    private httpService = inject(HttpService);
    private _snackbar = inject(MatSnackBar);


    ngOnInit() {
        combineLatest([
            this.route.queryParams,
            this.route.url
        ]).pipe(takeUntil(this.unsubscribeAll))
            .subscribe(async ([params, url]) => {
                const [locator, stringLocator] = this.buildLocatorFromParams(params);
                this.locator = stringLocator;
                this.facade = url[0].path;
                this.params = params;

                this.httpService.post<{ option: SelectOption[] }>(Net2BeepleMethodsEnum.retrieveEnumerationType, { enumerationClass: this.masterTypologyClass })
                    .subscribe((reply) => {
                        this.typologies = reply.option.filter(typology => this.facade === 'master' ? typology.code !== 'SYSTEM_CONFIG' : typology.code === 'SYSTEM_CONFIG');
                    });

                const [collection, partaker] = await firstValueFrom(this.dtypeService.retrieveMasterCollectionDN({
                    locator,
                    typology: this.facade === 'masterSystem' ? 'SYSTEM_CONFIG' : undefined,
                    noTruncate: false,
                }));

                const facadeUri = this.getUriFromFacade(this.facade);
                for (const loc of Object.keys(collection[facadeUri])) {
                    for (const repo of Object.values(collection[facadeUri][loc])) {
                        repo.sameLocator = this.isTheSameLocator(stringLocator, repo.belongsTo);
                        repo.belongsToCaption = this.getBelongsToCaption(repo.belongsTo, partaker);
                        this.repoList.push(repo);
                    }
                }
                this.captionLocator = this.getBelongsToCaption(this.locator, partaker);

                this.filteredRepoList = lodash.cloneDeep(this.repoList);
                this.pagination = {
                    length: this.filteredRepoList.length,
                    page: 0,
                    startIndex: 0,
                    endIndex: 10,
                    size: 10,
                    lastPage: Math.max(Math.ceil(this.filteredRepoList.length / 10), 1)
                };

                this.initialized.complete();
                this.loading = false;
            });

        this.searchControl.valueChanges
            .pipe(
                debounceTime(this.debounceTime),
                takeUntil(this.unsubscribeAll)
            )
            .subscribe(() => this.search());
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    ngAfterViewInit(): void {
        this.initialized.subscribe({
            complete: () => {
                this.changeDetector.detectChanges();
                if (this._paginator && this._sort) {
                    this._sort.sort({
                        id: 'belongsTo',
                        start: 'asc',
                        disableClear: true
                    });
                    this.paginateAndSort();

                    merge(
                        this._paginator.page,
                        this._sort.sortChange
                    )
                        .pipe(takeUntil(this.unsubscribeAll))
                        .subscribe(() => {
                            this.paginateAndSort();
                        });
                }
            }
        });
    }

    navigateToBelongsTo(repo: DTypeRepository) {
        if (repo.sameLocator) {
            return;
        }
        const split = repo.belongsTo.split(',');
        const firstLocatorSplit = split[0].split('=');
        let secondLocatorSplit = [];
        if (split.length > 1) {
            secondLocatorSplit = split[1].split('='); // Only used for customers, it is the brand code
        }
        switch (firstLocatorSplit[0]) {
            case 'TS':
                this.router.navigate(['/endpoints/edit-endpoint'], {
                    queryParams: {
                        ts_endpoint: this.params.ts_endpoint
                    }
                });
                break;
            case 'HS':
                this.router.navigate(['/endpoints/edit-httpendpoint'], {
                    queryParams: {
                        ts_endpoint: this.params.ts_endpoint,
                        http_endpoint: this.params.http_endpoint
                    }
                });
                break;
            case 'CL':
                this.router.navigate(['/endpoints/edit-beepleclient'], {
                    queryParams: {
                        ts_endpoint: this.params.ts_endpoint,
                        http_endpoint: this.params.http_endpoint,
                        client: this.params.client
                    }
                });
                break;
            case 'SV':
                this.router.navigate(['/endpoints/edit-httpendpoint'], {
                    queryParams: {
                        ts_endpoint: this.params.ts_endpoint,
                        http_endpoint: this.params.http_endpoint,
                        service: this.params.service
                    }
                });
                break;
            case 'EM':
                this.router.navigate(['/endpoints/edit-httpendpoint'], {
                    queryParams: {
                        ts_endpoint: this.params.ts_endpoint,
                        http_endpoint: this.params.http_endpoint,
                        client: this.params.client,
                        service: this.params.service,
                        company: this.params.company
                    }
                });
                break;
        }
    }

    getTypology(typology: String) {
        return this.typologies?.find(t => t.code === typology)?.value;
    }

    deleteRepository(repo: DTypeRepository) {
        // const dialogRef = this.fuseConfirmationService.open({
        //     title: this.transloco.translate('warning'),
        //     message: this.transloco.translate('deleteDTypeConfirm'),
        //     actions: {
        //         confirm: {
        //             label: this.transloco.translate('confirm')
        //         },
        //         cancel: {
        //             label: this.transloco.translate('cancel')
        //         }
        //     }
        // });

        // dialogRef.afterClosed().subscribe((result) => {
        //     if (result === 'confirmed') {
        //         this.httpService.post<any>(NextMethodsEnum.deleteDType, { uri: _uri })
        //         .pipe(
        //             catchError((error) => {
        //                 console.error(error);
        //                 this.dialogsService.openErrorDialog({
        //                     message: error?.error?.echo ? localize(error?.error?.echo) : undefined,
        //                 });
        //                 return throwError(() => error);
        //             })
        //         ).subscribe((reply) => {
        //             if (reply.status === 200) {
        //                 this.viewList = this.viewList.filter(item => item.uri !== _uri);
        //                 this.repoList = this.repoList.filter(item => item.uri !== _uri);
        //             }
        //             this._snackbar.open(this.transloco.translate('deletedDType', { name: _uri}), this.transloco.translate('accept'), { duration: 5000 });
        //         });
        //     }
        // });
        const name = this.localize.transform(repo.caption);
        this.dtypeService.deleteNextDType(repo.uri).subscribe({
            next: (reply) => {
                if (reply?.status === 200) {
                    this.viewList = this.viewList.filter(item => item.uri !== repo.uri);
                    this.filteredRepoList = this.filteredRepoList.filter(item => item.uri !== repo.uri);
                    this.repoList = this.repoList.filter(item => item.uri !== repo.uri);
                    this._snackbar.open(this.transloco.translate('deletedDType', { name }), this.transloco.translate('accept'), { duration: 5000 });
                } else {
                    this.dialogsService.openErrorDialog();
                }
            }, error: (err) => {
                console.error(err);
                this.dialogsService.openErrorDialog({
                    message: err?.error?.echo ? this.localize.transform(err?.error?.echo) : undefined,
                });
            }
        });
    }

    getAddCaption() {
        let facadeTranslationKey = '';
        switch (this.facade) {
            case 'complex':
                facadeTranslationKey = 'complex';
                break;
            case 'master':
                facadeTranslationKey = 'masterData';
                break;
            case 'form':
                facadeTranslationKey = 'form';
                break;
            case 'tasktype':
                facadeTranslationKey = 'taskTypes';
                break;
        }

        const split = this.locator.split(',');
        const firstLocatorSplit = split[0].split('=');
        switch (firstLocatorSplit[0]) {
            case 'OU':
                return this.transloco.translate('addDTypeToOU', { dtype: this.transloco.translate(facadeTranslationKey).toLowerCase() });
            case 'Endpoint':
                return this.transloco.translate('addDTypeToEndpoint', { dtype: this.transloco.translate(facadeTranslationKey).toLowerCase() });
            case 'EndpointTargetSystem':
                return this.transloco.translate('addDTypeToEndpointTargetSystem', { dtype: this.transloco.translate(facadeTranslationKey).toLowerCase() });
            default:
                return '';
        }
    }

    clearSearch(): void {
        this.searchControl.setValue('', { emitEvent: false });
        if (this._paginator) {
            this._paginator.pageIndex = 0;
        }
        this.filteredRepoList = lodash.cloneDeep(this.repoList);
        this.pagination = {
            length: this.filteredRepoList.length,
            page: 0,
            startIndex: 0,
            endIndex: this._paginator.pageSize,
            size: this._paginator.pageSize,
            lastPage: Math.max(Math.ceil(this.filteredRepoList.length / this._paginator.pageSize), 1)
        };
        this.paginateAndSort();
    }

    private paginateAndSort() {
        const repoList = [...this.filteredRepoList];
        this.sort(repoList);

        const begin = this._paginator.pageIndex * this._paginator.pageSize;
        const end = Math.min((this._paginator.pageSize * (this._paginator.pageIndex + 1)), repoList.length);
        this.viewList = repoList.slice(begin, end);
    }

    private sort(repoList: DTypeRepository[]) {
        const sortActive = this._sort.active;
        const sortDirection = this._sort.direction;
        repoList.sort((a: DTypeRepository, b: DTypeRepository) => {
            let compare = 0;
            if (sortActive === 'name') {
                compare = this.localize.transform((a as DTypeRepository).caption).localeCompare(this.localize.transform((b as DTypeRepository).caption));
            } else if (sortActive === 'belongsTo') {
                compare = this.compareBelongsTo(a, b);
            } else {
                compare = this.compareStatus(a as DTypeRepository, b as DTypeRepository);
            }
            return sortDirection === 'asc' ? compare : -compare;
        });
    }

    private search() {
        if (this.searchControl.value === '') {
            this.clearSearch();
            return;
        }
        if (this._paginator) {
            this._paginator.pageIndex = 0;
        }
        const list = lodash.cloneDeep(this.repoList);
        this.filteredRepoList = list.filter((r) => {
            return Utils.removeAccents(this.localize.transform(r.caption).toLowerCase()).includes(Utils.removeAccents(this.searchControl.value.toLowerCase()));
        });
        this.pagination = {
            length: this.filteredRepoList.length,
            page: 0,
            startIndex: 0,
            endIndex: this._paginator.pageSize,
            size: this._paginator.pageSize,
            lastPage: Math.max(Math.ceil(this.filteredRepoList.length / this._paginator.pageSize), 1)
        };
        this.paginateAndSort();
    }

    private compareBelongsTo(a: DTypeRepository, b: DTypeRepository) {
        const aBelongsTo = a.belongsTo.split(',')[0].split('=')[0];
        const bBelongsTo = b.belongsTo.split(',')[0].split('=')[0];
        if (aBelongsTo === bBelongsTo) {
            return this.localize.transform((a as DTypeRepository).caption).localeCompare(this.localize.transform((b as DTypeRepository).caption));
        } else {
            switch (aBelongsTo) {
                case 'EM':
                    return -1;
                case 'SV':
                    return bBelongsTo === 'EM' ? 1 : -1;
                case 'CL':
                    return bBelongsTo === 'EM' ? 1 : -1;
                case 'HS':
                    return bBelongsTo === 'TS' || bBelongsTo === 'OU' ? -1 : 1;
                case 'TS':
                    return bBelongsTo === 'OU' ? -1 : 1;
                case 'OU':
                    return 1;
            }
        }
    }

    private compareStatus(a: DTypeRepository, b: DTypeRepository) {
        if (a.status === b.status) {
            return this.localize.transform(a.caption).localeCompare(this.localize.transform(b.caption));
        }
        switch (a.status) {
            case DTypeStatus.LIVE:
                return -1;
            case DTypeStatus.STATIC:
                return b.status === DTypeStatus.LIVE ? 1 : -1;
            case DTypeStatus.DESIGN:
                return 1;
        }
    }

    private isTheSameLocator(locator: string, dtypeLocator: string) {
        const split = locator.split(',');
        const dtypeSplit = dtypeLocator.split(',');
        return dtypeSplit[0] === split[0];
    }

    private buildLocatorFromParams(params: Params): [Net2BeepleLocator, string] {
        const locator: Net2BeepleLocator = {
            OU: params?.ou,
            TS: params?.ts_endpoint,
            HS: params?.http_endpoint,
            CL: params?.client,
            SV: params?.service,
        };

        let stringLocator = '';
        if (params?.company) {
            stringLocator += `EM=${params.company},`;
        }
        if (params?.client) {
            stringLocator += `CL=${params.client},`;
        } else if (params?.service) {
            stringLocator += `SV=${params.service},`;
        }
        if (params?.http_endpoint) {
            stringLocator += `HS=${params.http_endpoint},`;
        }
        if (params?.ts_endpoint) {
            stringLocator += `TS=${params.ts_endpoint},`;
        }
        if (params?.ou) {
            stringLocator += `OU=${params.ou}`;
        }

        return [locator, stringLocator];
    }

    private getBelongsToCaption(locator: string, partaker: Partaker) {
        const split = locator.split(',');
        const firstLocatorSplit = split[0].split('=');
        let secondLocatorSplit = [];
        if (split.length > 1) {
            secondLocatorSplit = split[1].split('='); // Only used for customers, it is the brand code
        }
        let uri: string;
        switch (firstLocatorSplit[0]) {
            case 'OU':
                return partaker[OU_URI]?.[firstLocatorSplit[1]]?.name;
            case 'TS':
                return `${partaker[MasterEndpointTargetSystemUri]?.[firstLocatorSplit[1]]?.name} (${this.transloco.translate('endpointTargetSystem')})`;
            case 'HS':
                return `${partaker[HttpServiceEndpointUri]?.[firstLocatorSplit[1]]?.name} (${this.transloco.translate('httpEndpoints')})`;
            case 'CL':
                uri = `master://HS=${this.params.http_endpoint}/TS=${this.params.ts_endpoint}/OU=01/BeepleClient`;
                return `${partaker[uri]?.[firstLocatorSplit[1]]?.name} (${this.transloco.translate('beepleClients')})`;
            case 'SV':
                uri = `master://HS=${this.params.http_endpoint}/TS=${this.params.ts_endpoint}/OU=01/NextServicio_n2b`;
                return `${partaker[uri]?.[firstLocatorSplit[1]]?.name} (${this.transloco.translate('nextServices')})`;
            case 'EM':
                if (this.params.client) {
                    uri = `master://CL=${this.params.client}/HS=${this.params.http_endpoint}/TS=${this.params.ts_endpoint}/OU=01/Net2BeepleEmpresa`;
                } else if (this.params.service) {
                    uri = `master://SV=${this.params.service}/HS=${this.params.http_endpoint}/TS=${this.params.ts_endpoint}/OU=01/Net2BeepleEmpresa`;
                }
                return uri ? `${partaker[uri]?.[firstLocatorSplit[1]]?.name} (${this.transloco.translate('net2BeepleCompanies')})` : '';
            default:
                return '';
        }
    }

    private getUriFromFacade(facade: string) {
        switch (facade) {
            case 'master':
            case 'masterSystem':
                return MASTER_URI;
        }
    }
}
